import { LoDashStatic } from 'lodash';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { validateEmpty, validateMinMax } from 'utils/ValidateUtils';
import i18n from 'i18n';

export interface CustomGeofenceComponentModel {
  readonly event: UpdateEventListener<CustomGeofenceComponentModel>;
  readonly state: CustomGeofenceComponentState;
  onLongitudeChange: (value: string | undefined) => void;
  onLatitudeChange: (value: string | undefined) => void;
  onRadiusChange: (value: string | undefined) => void;
  validateLongitude: (value: string | undefined) => void;
  validateLatitude: (value: string | undefined) => void;
  validateRadius: (value: string | undefined) => void;
  onSave: () => void;
  onClear: () => void;
}

export type CustomGeofenceComponentProps = {
  readonly model: CustomGeofenceComponentModel;
};

export type CustomGeofenceComponentState = {
  readonly longitude: string | undefined;
  readonly latitude: string | undefined;
  readonly radius: string | undefined;
  readonly longitudeError: string | undefined;
  readonly latitudeError: string | undefined;
  readonly radiusError: string | undefined;
};

export class DefaultCustomGeofenceComponentModel implements CustomGeofenceComponentModel {
  private modelLongitude: string | undefined;
  private modelLatitude: string | undefined;
  private modelRadius: string | undefined;
  private modelLongitudeError: string | undefined;
  private modelLatitudeError: string | undefined;
  private modelRadiusError: string | undefined;
  private sendCount = 0;
  event: FireableUpdateEventListener<CustomGeofenceComponentModel>;

  constructor(
    public name: string,
    private value: SelectOptions[],
    private onChange: (
      type: string,
      value: SelectOptions[] | LoDashStatic
    ) => void
  ) {
    this.event =
      new FireableUpdateEventListener<CustomGeofenceComponentModel>();
  }

  get state(): CustomGeofenceComponentState {
    return {
      longitude: this.modelLongitude,
      latitude: this.modelLatitude,
      radius: this.modelRadius,
      longitudeError:
        this.sendCount === 0 ? undefined : this.modelLongitudeError,
      latitudeError: this.sendCount === 0 ? undefined : this.modelLatitudeError,
      radiusError: this.sendCount === 0 ? undefined : this.modelRadiusError
    };
  }

  validateDecimalPlaces(
    value: string,
    minPlaces?: number,
    maxPlaces?: number
  ): string | undefined {
    let decimalPlaces = 0;
    if (value.indexOf('.') === -1) {
      decimalPlaces = 0;
    } else {
      decimalPlaces = value.split('.')[1].length;
    }
    if (minPlaces && decimalPlaces < minPlaces) {
      return i18n.t('limitation.labels.minDecimalPlacesError', {
        count: minPlaces
      });
    }
    if (maxPlaces && decimalPlaces > maxPlaces) {
      return i18n.t('limitation.labels.maxDecimalPlacesError', {
        count: maxPlaces
      });
    }
  }

  validateLongitude = (value: string | undefined) => {
    this.modelLongitudeError =
      validateEmpty(value) ||
      validateMinMax(value, -180, 180) ||
      this.validateDecimalPlaces(value!, 4);
    this.updateState();
  };

  validateLatitude = (value: string | undefined) => {
    this.modelLatitudeError =
      validateEmpty(value) ||
      validateMinMax(value, -90, 90) ||
      this.validateDecimalPlaces(value!, 4);
    this.updateState();
  };

  validateRadius = (value: string | undefined) => {
    this.modelRadiusError =
      validateEmpty(value) ||
      validateMinMax(value, 0.1, 10) ||
      this.validateDecimalPlaces(value!, undefined, 1);
    this.updateState();
  };

  onLongitudeChange = (value: string | undefined) => {
    this.modelLongitude = value;
    this.validateLongitude(value);
  };

  onLatitudeChange = (value: string | undefined) => {
    this.modelLatitude = value;
    this.validateLatitude(value);
  };

  onRadiusChange = (value: string | undefined) => {
    this.modelRadius = value;
    this.validateRadius(value);
  };

  onSave = () => {
    this.sendCount++;
    // Validate all fields before saving
    this.validateLongitude(this.modelLongitude);
    this.validateLatitude(this.modelLatitude);
    this.validateRadius(this.modelRadius);

    // Only save if there are no validation errors
    if (
      !this.modelLongitudeError &&
      !this.modelLatitudeError &&
      !this.modelRadiusError
    ) {
      const newValue = `${this.modelLatitude},${this.modelLongitude},${this.modelRadius}`;
      this.onChange('customGeofence', [
        ...this.value,
        {
          label: '',
          value: newValue
        }
      ]);
      this.onClear();
    }
  };

  onClear = () => {
    this.modelLongitude = undefined;
    this.modelLatitude = undefined;
    this.modelRadius = undefined;
    this.modelLongitudeError = undefined;
    this.modelLatitudeError = undefined;
    this.modelRadiusError = undefined;
    this.updateState();
  };

  updateState = () => {
    this.event.fireEvent(this);
  };
}
